import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { handleNucleusResponse, OrderService } from '../../services';
import type { CreateCustomerOrderRequest } from '@billx/types';

export const useCustomerOrder = (
  businessId: string | undefined,
  outletId: string | undefined,
  customerId: string | undefined
) => {
  const queryClient = useQueryClient();

  const QUERY_KEY = ['customerOrder', businessId, outletId, customerId];

  // Fetch active order using the new endpoint
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      if (!customerId || !businessId) return null;
      const response = await OrderService.fetchOrderByCustomerId(
        customerId,
        businessId
      );
      return handleNucleusResponse(response).data;
    },
    enabled: !!customerId && !!businessId && !!outletId,
  });

  // Create order mutation
  const createOrder = useMutation({
    mutationFn: (data: CreateCustomerOrderRequest) => {
      if (!businessId) throw new Error('Business ID is required');
      return OrderService.createOrderForCustomer(data, businessId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const { data: activeOrder, ...restQuery } = query;

  return {
    activeOrder,
    queryKey: QUERY_KEY,
    ...restQuery,

    // Mutations
    createOrderForCustomer: createOrder,
  };
};
